// ============================================================
// 会話内の矛盾 → スタッフ全員へ即時メール（2026-10-03 前田さん）
//
// 「会話内で矛盾があったら即時スタッフメールにエスカレートして欲しい。メールは全員で見る。
//   それを見てどうするか、前田さんが開発チャットでフィードバックする」
//
// 検知は2系統:
//   1. ここの決定的な検知器（AI の判断に頼らない）
//   2. AI 自身が気づいた食い違い（report_contradiction ツール）
// 送信は agent/api.php action=escalate（記録＋店舗のスタッフ用アドレスへメール。30分の重複抑止つき）。
// ============================================================

import { postAgentAction } from './api';
import { buildTranscript } from './transcript';
import { getSessionMeta } from './sessionContext';
import { getOperatorName } from '../pages/reconcile/systemApi';
import { getStoreKey } from '../config/backend';
import { getPageContext } from './pageContext';
import type { AgentUiMessage } from './types';

export type EscalationKind =
  | 'save_mismatch' | 'false_send_claim' | 'stock_claim' | 'staff_correction' | 'ai_reported'
  | 'staff_feedback'
  | 'false_action_claim';   // ツールを呼ばずに「記入しました」等と答えた（2026-10-16 セッション2379）   // 「フィードバックを送る」ボタン（2026-10-03）

export interface Escalation {
  kind: EscalationKind;
  title: string;
  evidence: string[];
  contactCode?: string | null;
}

// ---------- 検知器（純粋関数） ----------

/** AI が「送信した」と言っているか（送信を促す言い方は除く） */
// 「送信されました」「無事に送信」もすり抜けていた（2026-10-12 セッション2179）
const SEND_CLAIM = /(送信|お送り|送付)(しました|いたしました|されました|が完了|済み|を完了|させていただきました)|無事に送信/;
const SEND_PROMPT = /［送信］|\[送信\]|送信ボタン|送信して(ください|いただけ)|お送りいただけ|送信をお願い/;

/**
 * 未送信の下書きがあり、送信の通知も無いのに「送信しました」と言った（2026-09-30 セッション1547）
 * @param sentSinceUser 直前のユーザー発言以降に、カードの［送信］が成功した件数
 */
export function detectFalseSendClaim(text: string, pendingDrafts: number, sentSinceUser: number): Escalation | null {
  if (pendingDrafts <= 0 || sentSinceUser > 0) return null;
  if (!SEND_CLAIM.test(text) || SEND_PROMPT.test(text)) return null;
  return {
    kind: 'false_send_claim',
    title: '未送信の下書きがあるのに、AI が「送信しました」と発言',
    evidence: [`AI の発言: ${text.slice(0, 200)}`, `未送信の下書き: ${pendingDrafts} 件（送信の通知なし）`],
  };
}

export interface AvailabilityFact { tool: string; available: number | null; label: string }

/** ツール結果から在庫の事実を取り出す（無ければ null） */
export function availabilityFromTool(tool: string, result: any): AvailabilityFact | null {
  if (!result || typeof result !== 'object') return null;
  if (tool === 'list_available_vehicles' && typeof result.available_count === 'number') {
    return { tool, available: result.available_count, label: String(result.basis ?? '') };
  }
  if (tool === 'search_vehicle_types' && Array.isArray(result.results) && result.results.length === 1) {
    const a = result.results[0]?.availability;
    if (a && typeof a.available_vehicles === 'number') {
      return { tool, available: a.available_vehicles, label: String(result.results[0]?.name ?? '') };
    }
  }
  if (tool === 'calculate_price' && typeof result.available_vehicles === 'number') {
    return { tool, available: result.available_vehicles, label: String(result.vehicle_type_name ?? '') };
  }
  return null;
}

const STOCK_POSITIVE = /(空き|在庫)(が|も)?(あり|ござい)|在庫(の)?確保(が)?(でき|しました)|確保できました/;
const STOCK_NEGATIVE = /ありません|ございません|満車|在庫なし|空きなし|0台|０台|在庫が0|在庫 0/;

/** 直近の在庫確認が 0 台なのに「空きあり」と言った（2026-09-26 記録#33 の型） */
export function detectStockClaim(text: string, fact: AvailabilityFact | null): Escalation | null {
  if (!fact || fact.available !== 0) return null;
  if (!STOCK_POSITIVE.test(text) || STOCK_NEGATIVE.test(text)) return null;
  return {
    kind: 'stock_claim',
    title: '在庫確認の結果は 0 台なのに、AI が「空きあり／確保できた」と発言',
    evidence: [`AI の発言: ${text.slice(0, 200)}`, `${fact.tool} の結果: 空き ${fact.available} 台（${fact.label}）`],
  };
}

/** スタッフが AI の誤りを指摘した発言か（review_signals と同じ型の言い回し） */
const CORRECTION = /違う|ちがう|じゃなく|ではなく|できていない|おかしい|間違|なぜ|なのに|してしまって|反映されて(い)?ない|まだ.{0,8}(だった|です|なので)|見れば.{0,12}わかり|すれば.{0,12}わかり/;
export function detectStaffCorrection(text: string, hasAssistantBefore: boolean): Escalation | null {
  if (!hasAssistantBefore) return null;
  const t = String(text || '').trim();
  if (t.length < 6 || t === '（スキップ）' || t.startsWith('（まとめて入力）')) return null;
  if (!CORRECTION.test(t)) return null;
  return {
    kind: 'staff_correction',
    title: 'スタッフが AI の回答・処理の誤りを指摘',
    evidence: [`スタッフの発言: ${t.slice(0, 300)}`],
  };
}

/**
 * ツールを1つも実行していないのに、予約データを変えたと発言した（2026-10-16 セッション2379:
 * 「ショートメモに『送迎不要』と記入いたしました」と答えたが、ツールの実行は無かった）。
 * 「完了」への相づちや経緯のまとめ（〜を確認しました・完了しております）は対象外。データを変える動詞だけを見る。
 */
const ACTION_CLAIM = /(記入|書き込み|入力|変更|更新|登録|追記|キャンセル|アサイン|反映|削除|設定|付与|解除)(いた)?しました|(記入|変更|更新|登録|追記|反映)済み/;
const ACTION_NEGATION = /(しませんでした|できませんでした|していません|まだ|未実施|ではありません|しましょうか|しますか|いたしますか)/;
export function detectFalseActionClaim(text: string, toolsSucceededThisTurn: number, lastUserText: string): Escalation | null {
  if (toolsSucceededThisTurn > 0) return null;
  const t = String(text || '');
  if (!ACTION_CLAIM.test(t) || ACTION_NEGATION.test(t)) return null;
  // 「完了」「ありがとう」への返答は経緯のまとめなので対象外
  if (/^(完了|ありがとう|了解|OK|ok|はい)[。！!]*$/.test(String(lastUserText || '').trim())) return null;
  return {
    kind: 'false_action_claim',
    title: 'ツールを実行していないのに、AI が予約データを変更したと発言',
    evidence: [`スタッフの指示: ${String(lastUserText || '').slice(0, 150)}`, `AI の発言: ${t.slice(0, 200)}`, 'このターンのツール実行: 0 件'],
  };
}

/** 保存後の照合で値が違った（ツール結果の unverified） */
export function detectSaveMismatch(tool: string, result: any): Escalation | null {
  const u = result && Array.isArray(result.unverified) ? result.unverified : [];
  if (u.length === 0) return null;
  return {
    kind: 'save_mismatch',
    title: `保存後に読み直した値が、AI が保存した値と違う（${tool}）`,
    evidence: [`一致しない項目: ${u.join(' / ')}`],
    contactCode: result.contact_code ?? null,
  };
}

// ---------- 送信 ----------

/** スタッフ全員へメール（サーバで記録・重複抑止）。結果はチャットに出すため返す */
export async function sendEscalation(esc: Escalation, ctx: {
  sessionId: string; uiMessages?: AgentUiMessage[]; transcript?: string;
}): Promise<{ ok: boolean; id?: number; deduped?: boolean; mailTo?: string; error?: string }> {
  const meta = getSessionMeta();
  const transcript = (ctx.transcript ?? buildTranscript((ctx.uiMessages ?? []).slice(-12))).slice(-5000);
  try {
    const r: any = await postAgentAction('escalate', {
      session_id: ctx.sessionId,
      contact_code: esc.contactCode ?? (meta.kind === 'reservation' ? meta.anchor : '') ?? '',
      kind: esc.kind,
      title: esc.title,
      evidence: esc.evidence,
      transcript,
      operator: getOperatorName() || '',
      store: getStoreKey(),
      page: String((getPageContext() as any)?.page ?? ''),
    });
    return { ok: !!r?.success, id: r?.id, deduped: !!r?.deduped, mailTo: r?.mail_to, error: r?.error ?? undefined };
  } catch (e: any) {
    return { ok: false, error: e?.message || String(e) };
  }
}

/** チャットに出す一行 */
export function escalationNotice(esc: Escalation, r: { ok: boolean; id?: number; deduped?: boolean; mailTo?: string; error?: string }): string {
  if (r.deduped) return `📣 同じ内容のエスカレーションは送信済みです（#${r.id}）。`;
  if (esc.kind === 'staff_feedback') {
    return r.ok
      ? `📝 フィードバックをスタッフ全員にメールしました（#${r.id}・${r.mailTo}）。ありがとうございます。`
      : `⚠ フィードバックのメール送信に失敗しました: ${r.error ?? '不明なエラー'}`;
  }
  return r.ok
    ? `📣 矛盾を検知したため、スタッフ全員にメールしました（#${r.id}・${r.mailTo}）: ${esc.title}`
    : `⚠ 矛盾を検知しましたが、スタッフへのメール送信に失敗しました: ${r.error ?? '不明なエラー'}（内容: ${esc.title}）`;
}
